import React, { useState, useEffect, useRef } from "react";
import { Button, Typography, Box, Grid, useMediaQuery, IconButton, CircularProgress, Alert, LinearProgress } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { PetFormHeader } from "../components/PetFormHeader";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { getPetFacialImages, RegisterFaceID, UploadArrayImages, } from "../services/api";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import { set } from "lodash";


const UploadSlot = ({ label, preview, onUpload, onRemove, index, disabled }) => {
  const [isHovered, setIsHovered] = useState(false);
  const fileInputRef = useRef(null);
  const isMobile = useMediaQuery("(max-width:900px)");

  const handleClick = () => {
    if (preview || disabled) {
      // If there's a preview, allow removal or replacement
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      onUpload(file, index);
    }
  };

  return (
    <Box
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        border: preview ? "2px solid #FFB60C" : "2px dashed #D9D9D9",
        borderRadius: 2,
        width: isMobile ? 70 : 90,
        height: isMobile ? 70 : 90,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: isHovered ? "#FFF9E6" : "#fff",
        cursor: disabled ? "not-allowed" : "pointer",
        mb: 1,
        position: "relative",
        overflow: "hidden",
        transition: "all 0.3s ease",
        borderColor: isHovered ? "#FFB60C" : (preview ? "#FFB60C" : "#D9D9D9"),
      }}
    >
      {preview ? (
        <>
          <Box
            component="img"
            src={preview}
            alt="Preview"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
          {isHovered && (
            <>
              {disabled ? (
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    borderRadius: "50%",
                    width: 32,
                    height: 32,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CheckIcon sx={{ color: "#4CAF50", fontSize: 24 }} />
                </Box>
              ) : (
                <>
                  {/* Change/Edit icon - top left */}
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      fileInputRef.current?.click();
                    }}
                    sx={{
                      position: "absolute",
                      top: 4,
                      left: 4,
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                      width: 24,
                      height: 24,
                      padding: 0,
                      "&:hover": {
                        backgroundColor: "#fff",
                      },
                    }}
                  >
                    <EditIcon sx={{ fontSize: 16, color: "#FFB60C" }} />
                  </IconButton>

                  {/* Remove/Close icon - top right */}
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(index);
                    }}
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                      width: 24,
                      height: 24,
                      padding: 0,
                      "&:hover": {
                        backgroundColor: "#fff",
                      },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16, color: "#FF4444" }} />
                  </IconButton>
                </>
              )}
            </>
          )}
        </>
      ) : (
        <>
          <AddIcon sx={{ color: isHovered && !disabled ? "#FFB60C" : "#FFB60C", fontSize: 32 }} />
          <Typography sx={{ fontSize: 13, color: isHovered && !disabled ? "#FFB60C" : "#A0A0A0", mt: 1 }}>
            {label}
          </Typography>
        </>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleFileChange}
        disabled={disabled}
      />
    </Box>
  );
};

const PetFaceID = () => {
  const isMobile = useMediaQuery("(max-width:900px)");
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const { petId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [showHelpImage, setShowHelpImage] = useState(false);
  const [showHelpImage2, setShowHelpImage2] = useState(false);
  const [petData, setPetData] = useState(location.state?.petData);
  const [faceCarouselIndex, setFaceCarouselIndex] = useState(0);
  const [noseCarouselIndex, setNoseCarouselIndex] = useState(0);

  // Image state management - 5 angles, each with 5 slots
  const [faceImagePreviews, setFaceImagePreviews] = useState(Array(5).fill(null).map(() => Array(5).fill(null)));
  const [faceImageFiles, setFaceImageFiles] = useState(Array(5).fill(null).map(() => Array(5).fill(null)));
  const [noseImagePreviews, setNoseImagePreviews] = useState(Array(5).fill(null).map(() => Array(5).fill(null)));
  const [noseImageFiles, setNoseImageFiles] = useState(Array(5).fill(null).map(() => Array(5).fill(null)));
  const [existingImageIndexes, setExistingImageIndexes] = useState({ face: [], nose: [] });

  // Submission state management
  const [buttonLoading, setButtonLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [BackColor, setBackColor] = useState("");
  const [UpdateMessage, setUpdateMessage] = useState("");

  const faceLabels = [t('FrontView'), t('LeftView'), t('RightView'), t('UpwardView'), t('DownwardView')];
  const noseLabels = [t('FrontView'), t('LeftView'), t('RightView'), t('UpwardView'), t('DownwardView')];
  const faceImages = ['/icons/faceFront.png', '/icons/faceLeft.png', '/icons/faceRight.png', '/icons/faceUp.png', '/icons/faceDown.png'];
  const noseImages = ['/icons/faceFront.png', '/icons/noseLeft.png', '/icons/noseRight.png', '/icons/faceUp.png', '/icons/faceDown.png'];

  const helpImageRef = useRef(null);
  const helpImageRef2 = useRef(null);

  // 图标样式
  const iconStyle = {
    width: "20px",
    height: "20px",
    marginLeft: "8px",
    cursor: "pointer",
    verticalAlign: "middle",
  };

  // 帮助图片样式
  const helpImageStyle = {
    position: "absolute",
    left: "100px",
    top: "0px",
    width: "200px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
    zIndex: 10,
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#FFF0CD",
    padding: "40px 0",
  };

  const helpImageStyle2 = {
    position: "absolute",
    left: "100px",
    top: "0px",
    width: "200px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
    zIndex: 10,
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#FFF0CD",
    padding: "40px 0",
  };

  // 处理图标点击
  const handleInfoClick = (e) => {
    e.stopPropagation(); // 阻止事件冒泡
    setShowHelpImage(!showHelpImage);
    if (!showHelpImage) {
      setFaceCarouselIndex(0); // Reset to first image when opening
    }
  };

  // 处理图标点击
  const handleInfoClick2 = (e) => {
    e.stopPropagation(); // 阻止事件冒泡
    setShowHelpImage2(!showHelpImage2);
    if (!showHelpImage2) {
      setNoseCarouselIndex(0); // Reset to first image when opening
    }
  };

  // Carousel navigation handlers
  const handleFacePrev = (e) => {
    e.stopPropagation();
    setFaceCarouselIndex((prev) => (prev === 0 ? faceImages.length - 1 : prev - 1));
  };

  const handleFaceNext = (e) => {
    e.stopPropagation();
    setFaceCarouselIndex((prev) => (prev === faceImages.length - 1 ? 0 : prev + 1));
  };

  const handleNosePrev = (e) => {
    e.stopPropagation();
    setNoseCarouselIndex((prev) => (prev === 0 ? noseImages.length - 1 : prev - 1));
  };

  const handleNoseNext = (e) => {
    e.stopPropagation();
    setNoseCarouselIndex((prev) => (prev === noseImages.length - 1 ? 0 : prev + 1));
  };

  // Fetch pet data if editing
  useEffect(() => {
    if (location.pathname.includes("edit") && petId) {
      setIsEditMode(true);
      setLoading(true);

      // Fetch pet facial images
      getPetFacialImages(petId)
        .then((response) => {
          const { faceImages, noseImages } = response.data;

          // Populate face image previews from URLs
          if (faceImages) {
            const facePreviewsArray = [
              faceImages.faceFrontUrls || [],
              faceImages.faceLeftUrls || [],
              faceImages.faceRightUrls || [],
              faceImages.faceUpperUrls || [],
              faceImages.faceLowerUrls || [],
            ];
            const existingFaceIndexes = [];
            facePreviewsArray.forEach((urls, angleIndex) => {
              urls.forEach((url, slotIndex) => {
                if (url) {
                  existingFaceIndexes.push({ angleIndex, slotIndex });
                }
              });
            });

            setFaceImagePreviews(facePreviewsArray.map(urls =>
              [...urls, ...Array(5 - urls.length).fill(null)]
            ));
            setFaceImageFiles(facePreviewsArray.map(urls =>
              [...urls, ...Array(5 - urls.length).fill(null)]
            ));
            setExistingImageIndexes(prev => ({ ...prev, face: existingFaceIndexes }));
          }

          // Populate nose image previews from URLs
          if (noseImages) {
            const nosePreviewsArray = [
              noseImages.noseFrontUrls || [],
              noseImages.noseLeftUrls || [],
              noseImages.noseRightUrls || [],
              noseImages.noseUpperUrls || [],
              noseImages.noseLowerUrls || [],
            ];
            const existingNoseIndexes = [];
            nosePreviewsArray.forEach((urls, angleIndex) => {
              urls.forEach((url, slotIndex) => {
                if (url) {
                  existingNoseIndexes.push({ angleIndex, slotIndex });
                }
              });
            });
            setNoseImagePreviews(nosePreviewsArray.map(urls =>
              [...urls, ...Array(5 - urls.length).fill(null)]
            ));
            setNoseImageFiles(nosePreviewsArray.map(urls =>
              [...urls, ...Array(5 - urls.length).fill(null)]
            ));
            setExistingImageIndexes(prev => ({ ...prev, nose: existingNoseIndexes }));
          }
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching facial images:", error);
          setLoading(false);
        });
    }
  }, [location, petId]);

  // 添加点击外部关闭图片的逻辑
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        helpImageRef.current &&
        !helpImageRef.current.contains(event.target)
      ) {
        setShowHelpImage(false);
      }
    };

    // 只有当图片显示时才添加事件监听
    if (showHelpImage) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showHelpImage]);

  // 添加点击外部关闭图片的逻辑
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        helpImageRef2.current &&
        !helpImageRef2.current.contains(event.target)
      ) {
        setShowHelpImage2(false);
      }
    };

    // 只有当图片显示时才添加事件监听
    if (showHelpImage2) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showHelpImage2]);

  const compressImage = async (file) => {
    const fileSizeMB = file.size / (1024 * 1024);
    
    // Reject files larger than 10MB
    if (fileSizeMB > 10) {
      setError(true);
      setBackColor("#d32f2f");
      setUpdateMessage(t('ImageTooLarge'));
      setTimeout(() => {
        setError(false);
      }, 2000);
    }
    
    // to ensure the total payload with 15+ images stays under Lambda's 6MB limit
    let quality;
    if (fileSizeMB <= 0.3) {
      quality = 1; 
    } else if (fileSizeMB <= 1) {
      quality = 0.3; 
    } else if (fileSizeMB <= 2) {
      quality = 0.2; 
    } else {
      quality = 0.1; 
    }
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                console.log(`Compressed ${file.name}: ${fileSizeMB.toFixed(2)}MB → ${(blob.size / (1024 * 1024)).toFixed(2)}MB`);
                resolve(compressedFile);
              } else {
                reject(new Error('Compression failed'));
              }
            },
            'image/jpeg',
            quality
          );
        };
        img.onerror = () => reject(new Error('Failed to load image'));
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
    });
  };

  // Handle face image upload with compression
  const handleFaceImageUpload = async (file, angleIndex, slotIndex) => {
    try {
      // Compress the image
      const compressedFile = await compressImage(file);
      
      // Create preview from compressed file
      const reader = new FileReader();
      reader.onloadend = () => {
        setFaceImagePreviews(prev => {
          const newPreviews = prev.map(row => [...row]);
          newPreviews[angleIndex][slotIndex] = reader.result;
          return newPreviews;
        });
      };
      reader.readAsDataURL(compressedFile);

      // Store compressed file
      setFaceImageFiles(prev => {
        const newFiles = prev.map(row => [...row]);
        newFiles[angleIndex][slotIndex] = compressedFile;
        return newFiles;
      });
    } catch (error) {
      console.error("Error compressing face image:", error);
      setError(true);
      setBackColor("#d32f2f");
      setUpdateMessage(error.message);
      setTimeout(() => {
        setError(false);
      }, 3000);
    }
  };

  // Handle face image removal
  const handleFaceImageRemove = (angleIndex, slotIndex) => {
    setFaceImagePreviews(prev => {
      const newPreviews = prev.map(row => [...row]);
      newPreviews[angleIndex][slotIndex] = null;
      return newPreviews;
    });
    setFaceImageFiles(prev => {
      const newFiles = prev.map(row => [...row]);
      newFiles[angleIndex][slotIndex] = null;
      return newFiles;
    });
  };

  // Handle nose image upload with compression
  const handleNoseImageUpload = async (file, angleIndex, slotIndex) => {
    try {
      // Compress the image
      const compressedFile = await compressImage(file);
      
      // Create preview from compressed file
      const reader = new FileReader();
      reader.onloadend = () => {
        setNoseImagePreviews(prev => {
          const newPreviews = prev.map(row => [...row]);
          newPreviews[angleIndex][slotIndex] = reader.result;
          return newPreviews;
        });
      };
      reader.readAsDataURL(compressedFile);

      // Store compressed file
      setNoseImageFiles(prev => {
        const newFiles = prev.map(row => [...row]);
        newFiles[angleIndex][slotIndex] = compressedFile;
        return newFiles;
      });
    } catch (error) {
      console.error("Error compressing nose image:", error);
      setError(true);
      setBackColor("#d32f2f");
      setUpdateMessage(error.message);
      setTimeout(() => {
        setError(false);
      }, 3000);
    }
  };

  // Handle nose image removal
  const handleNoseImageRemove = (angleIndex, slotIndex) => {
    setNoseImagePreviews(prev => {
      const newPreviews = prev.map(row => [...row]);
      newPreviews[angleIndex][slotIndex] = null;
      return newPreviews;
    });
    setNoseImageFiles(prev => {
      const newFiles = prev.map(row => [...row]);
      newFiles[angleIndex][slotIndex] = null;
      return newFiles;
    });
  };

  // Convert image file to base64 array
  const fileToBase64Array = (file) => {
    return new Promise((resolve, reject) => {
      if (!file) {
        resolve([]);
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        // Return as array containing the base64 string
        resolve([reader.result]);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setButtonLoading(true);
    setProgress(0);
    setError(false);
    setSuccess(false);

    try {
      const userId = Cookies.get("id");

      // Validate that ALL 5 face angles have at least 3 images each
      const missingAngles = [];
      faceImageFiles.forEach((angleSlots, index) => {
        const imageCount = angleSlots.filter(file => file !== null).length;
        if (imageCount < 3) {
          missingAngles.push(`${faceLabels[index]} (${imageCount}/3)`);
        }
      });

      if (missingAngles.length > 0) {
        setButtonLoading(false);
        setError(true);
        setBackColor("#d32f2f");
        setUpdateMessage(t('PleaseUploadFaceImages'));
        setTimeout(() => {
          setError(false);
        }, 3000);
        return;
      }

      // Convert all images to base64 arrays - collect all non-null images per angle
      const convertAngleImages = async (angleSlots) => {
        const validFiles = angleSlots.filter(file => file instanceof File);
        const base64Array = await Promise.all(validFiles.map(file => fileToBase64Array(file)));
        return base64Array.flat().filter(item => item.length > 0);
      };

      const [faceFrontArray, faceLeftArray, faceRightArray, faceUpperArray, faceLowerArray] = await Promise.all([
        convertAngleImages(faceImageFiles[0]),
        convertAngleImages(faceImageFiles[1]),
        convertAngleImages(faceImageFiles[2]),
        convertAngleImages(faceImageFiles[3]),
        convertAngleImages(faceImageFiles[4]),
      ]);

      const [noseFrontArray, noseLeftArray, noseRightArray, noseUpperArray, noseLowerArray] = await Promise.all([
        convertAngleImages(noseImageFiles[0]),
        convertAngleImages(noseImageFiles[1]),
        convertAngleImages(noseImageFiles[2]),
        convertAngleImages(noseImageFiles[3]),
        convertAngleImages(noseImageFiles[4]),
      ]);

      // Count total images to determine batch strategy
      const totalImages = faceFrontArray.length + faceLeftArray.length + faceRightArray.length + 
                         faceUpperArray.length + faceLowerArray.length + noseFrontArray.length + 
                         noseLeftArray.length + noseRightArray.length + noseUpperArray.length + noseLowerArray.length;
      
      console.log(`Total images to upload: ${totalImages}`);

      // Calculate number of batches needed (6 images per batch to stay under Lambda limit)
      const IMAGES_PER_BATCH = 6;
      const numberOfBatches = Math.ceil(totalImages / IMAGES_PER_BATCH);
      
      console.log(`Splitting into ${numberOfBatches} batch(es)`);

      // Helper function to split an array into batches
      const splitIntoBatches = (array, batchSize) => {
        const batches = [];
        for (let i = 0; i < array.length; i += batchSize) {
          batches.push(array.slice(i, i + batchSize));
        }
        return batches;
      };

      // Distribute images across batches evenly
      const allImages = [
        ...faceFrontArray.map(img => ({ img, type: 'faceFront' })),
        ...faceLeftArray.map(img => ({ img, type: 'faceLeft' })),
        ...faceRightArray.map(img => ({ img, type: 'faceRight' })),
        ...faceUpperArray.map(img => ({ img, type: 'faceUpper' })),
        ...faceLowerArray.map(img => ({ img, type: 'faceLower' })),
        ...noseFrontArray.map(img => ({ img, type: 'noseFront' })),
        ...noseLeftArray.map(img => ({ img, type: 'noseLeft' })),
        ...noseRightArray.map(img => ({ img, type: 'noseRight' })),
        ...noseUpperArray.map(img => ({ img, type: 'noseUpper' })),
        ...noseLowerArray.map(img => ({ img, type: 'noseLower' })),
      ];

      const imageBatches = splitIntoBatches(allImages, IMAGES_PER_BATCH);

      // Collect all uploaded URLs from all batches
      const allUploadedUrls = {
        faceFrontArray: [],
        faceLeftArray: [],
        faceRightArray: [],
        faceUpperArray: [],
        faceLowerArray: [],
        noseFrontArray: [],
        noseLeftArray: [],
        noseRightArray: [],
        noseUpperArray: [],
        noseLowerArray: [],
      };

      // Upload images in batches
      for (let i = 0; i < imageBatches.length; i++) {
        const batch = imageBatches[i];
        console.log(`Uploading batch ${i + 1}/${imageBatches.length} with ${batch.length} images`);

        // Update progress based on batch completion (0-90% range for uploads)
        const batchProgress = Math.floor(((i) / imageBatches.length) * 90);
        setProgress(batchProgress);

        // Prepare batch data
        const batchData = {
          faceFrontArray: batch.filter(item => item.type === 'faceFront').map(item => item.img),
          faceLeftArray: batch.filter(item => item.type === 'faceLeft').map(item => item.img),
          faceRightArray: batch.filter(item => item.type === 'faceRight').map(item => item.img),
          faceUpperArray: batch.filter(item => item.type === 'faceUpper').map(item => item.img),
          faceLowerArray: batch.filter(item => item.type === 'faceLower').map(item => item.img),
          noseFrontArray: batch.filter(item => item.type === 'noseFront').map(item => item.img),
          noseLeftArray: batch.filter(item => item.type === 'noseLeft').map(item => item.img),
          noseRightArray: batch.filter(item => item.type === 'noseRight').map(item => item.img),
          noseUpperArray: batch.filter(item => item.type === 'noseUpper').map(item => item.img),
          noseLowerArray: batch.filter(item => item.type === 'noseLower').map(item => item.img),
          petId,
          userId,
          business: "PetPetClub",
        };

        // Call API for this batch
        const response = await UploadArrayImages(batchData);

        if (response.status === 201) {
          const uploadedUrls = response.data;
          
          // Collect URLs from this batch
          allUploadedUrls.faceFrontArray.push(...(uploadedUrls.faceFrontArray || []));
          allUploadedUrls.faceLeftArray.push(...(uploadedUrls.faceLeftArray || []));
          allUploadedUrls.faceRightArray.push(...(uploadedUrls.faceRightArray || []));
          allUploadedUrls.faceUpperArray.push(...(uploadedUrls.faceUpperArray || []));
          allUploadedUrls.faceLowerArray.push(...(uploadedUrls.faceLowerArray || []));
          allUploadedUrls.noseFrontArray.push(...(uploadedUrls.noseFrontArray || []));
          allUploadedUrls.noseLeftArray.push(...(uploadedUrls.noseLeftArray || []));
          allUploadedUrls.noseRightArray.push(...(uploadedUrls.noseRightArray || []));
          allUploadedUrls.noseUpperArray.push(...(uploadedUrls.noseUpperArray || []));
          allUploadedUrls.noseLowerArray.push(...(uploadedUrls.noseLowerArray || []));

          console.log(`Batch ${i + 1} uploaded successfully`);
          
          // Update progress after successful batch upload
          const completedProgress = Math.floor(((i + 1) / imageBatches.length) * 90);
          setProgress(completedProgress);
        } else {
          throw new Error(`Batch ${i + 1} upload failed with status ${response.status}`);
        }
      }

      // Set progress to 90% before final registration
      setProgress(90);

      // Helper to combine existing URLs with new URLs
      const combineUrls = (existingFiles, newUrls) => {
        const existingUrls = existingFiles.filter(file => typeof file === 'string');
        return [...existingUrls, ...newUrls];
      };

      // Prepare final payload with all uploaded URLs
      const finalFaceIdPayload = {
        faceFrontArray: combineUrls(faceImageFiles[0], allUploadedUrls.faceFrontArray),
        faceLeftArray: combineUrls(faceImageFiles[1], allUploadedUrls.faceLeftArray),
        faceRightArray: combineUrls(faceImageFiles[2], allUploadedUrls.faceRightArray),
        faceUpperArray: combineUrls(faceImageFiles[3], allUploadedUrls.faceUpperArray),
        faceLowerArray: combineUrls(faceImageFiles[4], allUploadedUrls.faceLowerArray),
        noseFrontArray: combineUrls(noseImageFiles[0], allUploadedUrls.noseFrontArray),
        noseLeftArray: combineUrls(noseImageFiles[1], allUploadedUrls.noseLeftArray),
        noseRightArray: combineUrls(noseImageFiles[2], allUploadedUrls.noseRightArray),
        noseUpperArray: combineUrls(noseImageFiles[3], allUploadedUrls.noseUpperArray),
        noseLowerArray: combineUrls(noseImageFiles[4], allUploadedUrls.noseLowerArray),
        petId,
        userId,
        business: "PetPetClub",
      };

      console.log('All batches uploaded successfully, registering face ID...');

      // Register face ID with all uploaded URLs
      const faceResponse = await RegisterFaceID(finalFaceIdPayload);
      if (faceResponse.status === 201) {
        setProgress(100);
        setButtonLoading(false);
        setSuccess(true);
        setBackColor("#4CAF50");
        setUpdateMessage(t('FaceIDRegisteredSuccessfully'));
        setTimeout(() => {
          setSuccess(false);
          setProgress(0);
        }, 2000);
      }
    } catch (error) {
      console.error("Error registering face ID:", error);
      setProgress(0);
      setError(true);
      setBackColor("#d32f2f");
      setUpdateMessage(t('ErrorSavingPet'));
      setTimeout(() => {
        setError(false);
      }, 2000);
    } finally {
      setButtonLoading(false);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "#FCF8F3",
        minHeight: "100vh",
        pt: isMobile ? 12 : 16,
      }}
    >
      <Navbar />
      <Box
        sx={{
          maxWidth: 1440,
          mx: "auto",
          mb: { xs: 5, md: 10 },
          px: { xs: 2, sm: 3, md: 0 },
        }}
      >
        <PetFormHeader
          currentPageId="faceID"
          isEditMode={isEditMode}
          petId={isEditMode ? petId : null}
          handlePageNavigation={() => { }}
          petData={petData}
        />
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 20,
              minHeight: "100vh",
            }}
          >
            <Typography variant="h6" color="text.secondary">
              <img
                src="https://petpetclub.s3.ap-southeast-1.amazonaws.com/user-uploads/pets/68cb893a0feda12f2dbcc02b/68d4c8ce22baedfbe8b852dd.gif"
                alt="Loading"
                style={{
                  width: "150px",
                  height: "auto",
                  mixBlendMode: "multiply",
                }}
              />
              <div
                style={{
                  fontWeight: "500px",
                  fontSize: "15px",
                  textAlign: "center",
                  fontFamily: "Lucida Console",

                }}>
                {t('Loading')}
              </div>
            </Typography>
          </Box>
        ) : (
          <>
            <Box>
              <Typography fontWeight={700} sx={{ fontSize: 18, mb: 4, px: 3 }}>
                {t('FacialRecognition')}
              </Typography>
              <Grid
                container
                spacing={isMobile ? 4 : 6}
                sx={{ px: 3, mb: 4 }}
                direction={isMobile ? "column" : "row"}
              >
                {/* Face Photos Section */}
                <Grid item xs={12} md={6}>
                  <Typography
                    fontWeight={700}
                    sx={{
                      fontSize: 16,
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      position: "relative",
                    }}
                  >
                    {t('FacePhoto')}
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" style={iconStyle} onClick={handleInfoClick} title="点击查看帮助图片" fill="none">
                      <path d="M9.9974 18.8307C14.5998 18.8307 18.3307 15.0998 18.3307 10.4974C18.3307 5.89502 14.5998 2.16406 9.9974 2.16406C5.39502 2.16406 1.66406 5.89502 1.66406 10.4974C1.66406 15.0998 5.39502 18.8307 9.9974 18.8307Z" stroke="#969696" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M7.57812 7.99852C7.77405 7.44158 8.16075 6.97194 8.66975 6.6728C9.17876 6.37365 9.7772 6.2643 10.3591 6.36411C10.941 6.46393 11.4688 6.76646 11.849 7.21813C12.2292 7.6698 12.4373 8.24146 12.4365 8.83185C12.4365 10.4985 9.93646 11.3319 9.93646 11.3319" stroke="#969696" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M10 14.6641H10.0083" stroke="#969696" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {/* 帮助图片，点击感叹号时显示 */}
                    {showHelpImage && (
                      <div ref={helpImageRef} style={helpImageStyle}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", position: "relative" }}>
                          {/* Left Arrow */}
                          <button
                            onClick={handleFacePrev}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              fontSize: "24px",
                              color: "#666",
                              padding: "0 10px",
                            }}
                          >
                            ‹
                          </button>

                          {/* Image Container */}
                          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
                            <img
                              src={faceImages[faceCarouselIndex]}
                              alt="宠物牌号码位置示例"
                              style={{ width: "100%", height: "auto", objectFit: "contain" }}
                            />
                            <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#050505" }}>
                              {faceLabels[faceCarouselIndex]}
                            </Typography>
                          </div>

                          {/* Right Arrow */}
                          <button
                            onClick={handleFaceNext}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              fontSize: "24px",
                              color: "#666",
                              padding: "0 10px",
                            }}
                          >
                            ›
                          </button>
                        </div>

                        {/* Dots Indicator */}
                        <div style={{ display: "flex", justifyContent: "center", gap: "8px", marginTop: "15px" }}>
                          {faceImages.map((_, index) => (
                            <span
                              key={index}
                              onClick={(e) => {
                                e.stopPropagation();
                                setFaceCarouselIndex(index);
                              }}
                              style={{
                                width: "8px",
                                height: "8px",
                                borderRadius: "50%",
                                backgroundColor: index === faceCarouselIndex ? "#FFB60C" : "#D9D9D9",
                                cursor: "pointer",
                                transition: "background-color 0.3s",
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </Typography>
                  <Box
                    sx={{
                      background: "#fff",
                      borderRadius: 2,
                      p: 3,
                      mb: 2,
                      width: isMobile ? "100%" : 600,
                    }}
                  >
                    {faceLabels.map((label, angleIdx) => (
                      <Box key={label} sx={{ mb: 3 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 300, color: "#969696", mb: 2 }}>
                          {label} ({t("Minimum3")})
                          <span style={{ color: "red", marginLeft: 4 }}>*</span>
                        </Typography>
                        <Grid container spacing={2}>
                          {Array(5).fill(null).map((_, slotIdx) => (
                            <Grid
                              item
                              xs={6}
                              sm={4}
                              md={2.4}
                              key={`${angleIdx}-${slotIdx}`}
                              sx={{ display: "flex", justifyContent: "center" }}
                            >
                              <UploadSlot
                                preview={faceImagePreviews[angleIdx][slotIdx]}
                                onUpload={existingImageIndexes.face.some(index => index.angleIndex === angleIdx && index.slotIndex === slotIdx) ? () => {} : (file) => handleFaceImageUpload(file, angleIdx, slotIdx)}
                                onRemove={existingImageIndexes.face.some(index => index.angleIndex === angleIdx && index.slotIndex === slotIdx) ? () => {} : () => handleFaceImageRemove(angleIdx, slotIdx)}
                                index={slotIdx}
                                disabled={existingImageIndexes.face.some(index => index.angleIndex === angleIdx && index.slotIndex === slotIdx)}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    ))}
                  </Box>
                  <Typography sx={{ color: "#A0A0A0", fontSize: 13, mb: 4, width: isMobile ? "100%" : 400, }}>
                    {t('photoInfo')}
                  </Typography>
                </Grid>
                {/* Nose Photos Section */}
                <Grid item xs={12} md={6}>
                  <Typography
                    fontWeight={700}
                    sx={{
                      fontSize: 16,
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      position: "relative",
                    }}
                  >
                    {t('NosePhoto')}
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" style={iconStyle} onClick={handleInfoClick2} title="点击查看帮助图片" fill="none">
                      <path d="M9.9974 18.8307C14.5998 18.8307 18.3307 15.0998 18.3307 10.4974C18.3307 5.89502 14.5998 2.16406 9.9974 2.16406C5.39502 2.16406 1.66406 5.89502 1.66406 10.4974C1.66406 15.0998 5.39502 18.8307 9.9974 18.8307Z" stroke="#969696" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M7.57812 7.99852C7.77405 7.44158 8.16075 6.97194 8.66975 6.6728C9.17876 6.37365 9.7772 6.2643 10.3591 6.36411C10.941 6.46393 11.4688 6.76646 11.849 7.21813C12.2292 7.6698 12.4373 8.24146 12.4365 8.83185C12.4365 10.4985 9.93646 11.3319 9.93646 11.3319" stroke="#969696" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M10 14.6641H10.0083" stroke="#969696" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {/* 帮助图片，点击感叹号时显示 */}
                    {showHelpImage2 && (
                      <div ref={helpImageRef2} style={helpImageStyle2}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", position: "relative" }}>
                          {/* Left Arrow */}
                          <button
                            onClick={handleNosePrev}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              fontSize: "24px",
                              color: "#666",
                              padding: "0 10px",
                            }}
                          >
                            ‹
                          </button>

                          {/* Image Container */}
                          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
                            <img
                              src={noseImages[noseCarouselIndex]}
                              alt="宠物牌号码位置示例"
                              style={{ width: "100%", height: "auto", objectFit: "contain" }}
                            />
                            <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#050505" }}>
                              {noseLabels[noseCarouselIndex]}
                            </Typography>
                          </div>

                          {/* Right Arrow */}
                          <button
                            onClick={handleNoseNext}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              fontSize: "24px",
                              color: "#666",
                              padding: "0 10px",
                            }}
                          >
                            ›
                          </button>
                        </div>

                        {/* Dots Indicator */}
                        <div style={{ display: "flex", justifyContent: "center", gap: "8px", marginTop: "15px" }}>
                          {noseImages.map((_, index) => (
                            <span
                              key={index}
                              onClick={(e) => {
                                e.stopPropagation();
                                setNoseCarouselIndex(index);
                              }}
                              style={{
                                width: "8px",
                                height: "8px",
                                borderRadius: "50%",
                                backgroundColor: index === noseCarouselIndex ? "#FFB60C" : "#D9D9D9",
                                cursor: "pointer",
                                transition: "background-color 0.3s",
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </Typography>
                  <Box
                    sx={{
                      background: "#fff",
                      borderRadius: 2,
                      p: 3,
                      mb: 2,
                      width: isMobile ? "100%" : "100%",
                    }}
                  >
                    {noseLabels.map((label, angleIdx) => (
                      <Box key={label} sx={{ mb: 3 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 300, color: "#969696", mb: 2 }}>
                          {label}
                        </Typography>
                        <Grid container spacing={2}>
                          {Array(5).fill(null).map((_, slotIdx) => (
                            <Grid
                              item
                              xs={6}
                              sm={4}
                              md={2.4}
                              key={`${angleIdx}-${slotIdx}`}
                              sx={{ display: "flex", justifyContent: "center" }}
                            >
                              <UploadSlot
                                preview={noseImagePreviews[angleIdx][slotIdx]}
                                onUpload={existingImageIndexes.nose.some(index => index.angleIndex === angleIdx && index.slotIndex === slotIdx) ? () => {} : (file) => handleNoseImageUpload(file, angleIdx, slotIdx)}
                                onRemove={existingImageIndexes.nose.some(index => index.angleIndex === angleIdx && index.slotIndex === slotIdx) ? () => {} : () => handleNoseImageRemove(angleIdx, slotIdx)}
                                index={slotIdx}
                                disabled={existingImageIndexes.nose.some(index => index.angleIndex === angleIdx && index.slotIndex === slotIdx)}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    ))}
                  </Box>
                  <Typography sx={{ color: "#A0A0A0", fontSize: 13, mb: 4, width: isMobile ? "100%" : 400, }}>
                    {t('photoInfo')}
                  </Typography>
                </Grid>
              </Grid>

              {/* Bottom Buttons */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  alignItems: isMobile ? "stretch" : "center",
                  justifyContent: { xs: "center", md: "flex-end" },
                  mt: { xs: 5, md: 10 },
                  mr: 3,
                  gap: 2,
                }}
              >
                {/* Loading Progress Bar - Desktop (left of Save button) */}
                {!isMobile && buttonLoading && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      minWidth: 200,
                      mr: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: 14,
                        color: "#FFB60C",
                        mb: 0.5,
                      }}
                    >
                      {Math.floor(progress)}%
                    </Typography>
                    <Box sx={{ width: "100%" }}>
                      <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          background: "#FFF0CD",
                          "& .MuiLinearProgress-bar": {
                            background: "#FFB60C",
                          },
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "#B0B0B0",
                        mt: 0.5,
                      }}
                    >
                      {progress < 100 ? t('Uploading') || 'Uploading...' : t('Complete') || 'Complete!'}
                    </Typography>
                  </Box>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  disabled={buttonLoading}
                  sx={{
                    backgroundColor: "#FFB60C",
                    color: "#050505",
                    minWidth: 120,
                    "&:hover": { backgroundColor: "#FFB60C" },
                    "&:disabled": { background: "rgba(255, 182, 12, 0.5)" },
                    cursor: buttonLoading ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                  }}
                  onClick={handleSubmit}
                >
                  {t('Save')}
                  {buttonLoading && (
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                      }}
                    >
                      <CircularProgress size={15} color="inherit" />
                    </span>
                  )}
                </Button>

                {/* Loading Progress Bar - Mobile (between Save and Cancel) */}
                {isMobile && buttonLoading && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      width: "100%",
                      py: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: 14,
                        color: "#FFB60C",
                        mb: 0.5,
                      }}
                    >
                      {Math.floor(progress)}%
                    </Typography>
                    <Box sx={{ width: "100%" }}>
                      <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          background: "#FFF0CD",
                          "& .MuiLinearProgress-bar": {
                            background: "#FFB60C",
                          },
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "#B0B0B0",
                        mt: 0.5,
                      }}
                    >
                      {progress < 100 ? t('Uploading') : t('Complete')}
                    </Typography>
                  </Box>
                )}

                <Button
                  type="button"
                  variant="outlined"
                  color="text.secondary"
                  onClick={() => navigate("/pet-management")}
                  sx={{
                    minWidth: 120,
                    "&:hover": {
                      backgroundColor: "black",
                      color: "white",
                    },
                  }}
                >
                  {t('cancel')}
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Box>
      <Footer />
      <Box
        sx={{
          position: "fixed",
          bottom: 20,
          right: (success || error) ? 20 : -500,
          backgroundColor: BackColor,
          color: "#fff",
          padding: isMobile ? "12px 18px" : "16px 24px",
          borderRadius: 2,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          zIndex: 9999,
          minWidth: 250,
          display: "flex",
          alignItems: "center",
          gap: 2,
          transition: (success || error)
            ? "right 0.4s ease-out"
            : "right 0.4s ease-in",
        }}
      >
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            backgroundColor: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {error ? (
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke={BackColor}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20 6L9 17L4 12"
                stroke={BackColor}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </Box>
        <Typography fontWeight={600} fontSize={isMobile ? 12 : 16}>
          {UpdateMessage}
        </Typography>
      </Box>
    </Box>
  );
};

export default PetFaceID;
